// external libs
import { createReducer, createActions } from 'reduxsauce'
import Immutable from 'seamless-immutable'
import * as uuid from 'uuid'

// config
import { config } from '../../Config/ConfigLoader'

// sort
import { sortObjectByProps } from '../SortFunctions'
import { parseApiError } from '../utils/utils'

/* ------------- Types and Action Creators ------------- */

export const NOTIFICATION_TYPES = {
  success: 'success',
  error: 'error',
  info: 'info',
}

export type NotificationType = {
  type: 'success' | 'error' | 'info',
  message: string,
  title: string,
  translate: boolean,
}

const { Types, Creators } = createActions({
  addToAllErrors: ['error', 'errorType', 'action'],
  onActionFailure: ['error', 'errorType'],
  onChangeRequestFailure: ['error', 'errorType'],
  onErrorsReset: null,
  onSetFetching: ['fetching'],
  onStartFetching: null,
  onStopFetching: null,
  onChangeRedirectUrl: ['redirectUrl', 'redirectState'],
  translationsRequestSuccess: ['translations'],
  errorTranslationsRequestSuccess: ['errorTranslations'],
  setInsuranceCompanies: ['insuranceCompanies'],
  onGetDefaultContentRequestLoaderOff: null,
  onGetDefaultContentRequestSuccess: null,
  setErrorsTimer: null,
  resetErrorsTimer: null,
  onChangeShowCallbackOnError: ['showCallbackOnError', 'error'],
  addErrorNotification: ['message', 'title', 'translate'],
  addSuccessNotification: ['message', 'title', 'translate'],
  addInfoNotification: ['message', 'title', 'translate'],
  addNotification: ['type', 'message', 'title', 'translate'],
  clearNotifications: null,
  initApp: null,
  logToStackdriver: ['logName', 'data', 'severity'],
})

export const GeneralTypes = Types
export default Creators

/* ------------- Initial State ------------- */

export const ERROR_TYPES = {
  INFO: 0,
  WARNING: 1,
  CRITICAL: 2,
}

export const INITIAL_STATE = Immutable({
  // eslint-disable-line
  errorsTimer: null,
  error: {},
  errors: [],
  apiErrors: [],
  allErrors: [],
  fetching: 0,
  redirectUrl: '',
  redirectState: null,
  translations: config.getDefaultTranslations(),
  errorTranslations: {},
  insuranceCompanies: {},
  showCallbackOnError: false,
  notificationToShow: [],
})

/* ------------- Reducers ------------- */

export const addToAllErrorsR = (state, { error, errorType, action }) => {
  const allErrors = state.allErrors.asMutable()
  if (!errorType) {
    errorType = ERROR_TYPES.WARNING
  }
  allErrors.push({ error, type: errorType, action, time: Date.now() })
  return state.merge({ allErrors })
}

export const onActionFailureR = (state, { error, errorType }) => {
  const errors = state.errors.asMutable()
  if (!errorType) {
    errorType = ERROR_TYPES.WARNING
  }
  errors.push({ error, type: errorType })
  return state.merge({ errors })
}

export const onChangeRequestFailureR = (state, { error, errorType }) => {
  if (error.response && error.response.status === 401) {
    return state
  }
  const errors = state.errors.asMutable()
  if (!errorType) {
    errorType = ERROR_TYPES.CRITICAL
  }
  errors.push({ error, type: errorType })
  return state.merge({ errors })
}

export const resetErrors = (state) => state.merge({ errors: INITIAL_STATE.errors })

export const setFetching = (state, { fetching }) => state.merge({ fetching })

export const startFetching = (state) => setFetching(state, { fetching: state.fetching + 1 })

export const stopFetching = (state) => setFetching(state, { fetching: state.fetching > 0 ? state.fetching - 1 : 0 })

export const onChangeRedirectUrlR = (state, { redirectUrl, redirectState }) =>
  state.merge({ redirectUrl, redirectState: redirectState || null })

export const translationsRequestSuccessR = (state, { translations }) => state.merge({ translations })

export const errorTranslationsRequestSuccessR = (state, { errorTranslations }) => state.merge({ errorTranslations })

export const setInsuranceCompaniesR = (state, { insuranceCompanies }) =>
  state.merge({ insuranceCompanies: sortObjectByProps(insuranceCompanies, 'shortName') })

export const setErrorsTimerR = (state) => state.merge({ errorsTimer: new Date().getTime() })

export const resetErrorsTimerR = (state) => state.merge({ errorsTimer: INITIAL_STATE.errorsTimer })

const onChangeShowCallbackOnErrorR = (state, { showCallbackOnError, error }) => {
  let immutableError = Immutable(error)
  if (error) {
    const apiErrors = parseApiError(immutableError.getIn(['data', 'apiValidationError'], []))
    if (apiErrors && apiErrors.length > 0) {
      return state
        .set('showCallbackOnError', true)
        .set('apiErrors', apiErrors)
        .set('error', apiErrors)
    }
  }
  return state
    .set('showCallbackOnError', showCallbackOnError)
    .set('apiErrors', [])
    .set('error', error ? immutableError.setIn(['config', 'headers', 'Authorization'], 'Bearer masked') : undefined)
}

export const addNotificationR = (state, { type, message, title = '', translate = false }) =>
  state.merge({
    notificationToShow: [...state.notificationToShow, { type, message, title, translate }],
  })

export const addError = (state, { message, title = '', translate = false }) =>
  addNotificationR(state, {
    type: NOTIFICATION_TYPES.error,
    message,
    title,
    translate,
  })

export const addSuccess = (state, { message, title = '', translate = false }) =>
  addNotificationR(state, {
    type: NOTIFICATION_TYPES.success,
    message,
    title,
    translate,
  })

export const addInfo = (state, { message, title = '', translate = false }) =>
  addNotificationR(state, {
    type: NOTIFICATION_TYPES.info,
    message,
    title,
    translate,
  })

export const clearNotificationsR = (state) => state.merge({ notificationToShow: [] })

const logToStackdriverR = (state) => {
  if (!state.logUuid) {
    return state.setIn(['logUuid'], uuid.v4())
  }
  return state
}

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.ADD_TO_ALL_ERRORS]: addToAllErrorsR,
  [Types.ON_ACTION_FAILURE]: onActionFailureR,
  [Types.ON_CHANGE_REQUEST_FAILURE]: onChangeRequestFailureR,
  [Types.ON_ERRORS_RESET]: resetErrors,
  [Types.ON_SET_FETCHING]: setFetching,
  [Types.ON_START_FETCHING]: startFetching,
  [Types.ON_STOP_FETCHING]: stopFetching,
  [Types.ON_CHANGE_REDIRECT_URL]: onChangeRedirectUrlR,
  [Types.TRANSLATIONS_REQUEST_SUCCESS]: translationsRequestSuccessR,
  [Types.ERROR_TRANSLATIONS_REQUEST_SUCCESS]: errorTranslationsRequestSuccessR,
  [Types.SET_INSURANCE_COMPANIES]: setInsuranceCompaniesR,
  [Types.ON_GET_DEFAULT_CONTENT_REQUEST_LOADER_OFF]: (state) => state,
  [Types.ON_GET_DEFAULT_CONTENT_REQUEST_SUCCESS]: (state) => state,
  [Types.SET_ERRORS_TIMER]: setErrorsTimerR,
  [Types.RESET_ERRORS_TIMER]: resetErrorsTimerR,
  [Types.ON_CHANGE_SHOW_CALLBACK_ON_ERROR]: onChangeShowCallbackOnErrorR,
  [Types.ADD_ERROR_NOTIFICATION]: addError,
  [Types.ADD_SUCCESS_NOTIFICATION]: addSuccess,
  [Types.ADD_INFO_NOTIFICATION]: addInfo,
  [Types.ADD_NOTIFICATION]: addNotificationR,
  [Types.CLEAR_NOTIFICATIONS]: clearNotificationsR,
  [Types.LOG_TO_STACKDRIVER]: logToStackdriverR,
  [Types.INIT_APP]: (state) => state,
})
